import { CircleHelp, Info } from "lucide-react";
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type EnhancedTooltipTriggerVariant = "icon" | "callout";
export type EnhancedTooltipSide = "top" | "bottom";

export interface EnhancedTooltipProps {
  content: React.ReactNode;
  title?: string;
  triggerLabel: string;
  triggerVariant?: EnhancedTooltipTriggerVariant;
  /** Custom trigger icon for the "icon" variant; defaults to the Info icon. */
  triggerIcon?: React.ReactNode;
  /** Replaces the default trigger styling for controls with their own design. */
  triggerClassName?: string;
  /** Keeps an unavailable action focusable so its explanation can be read. */
  ariaDisabled?: boolean;
  /** Also open on tap/click, including browsers that do not focus clicked buttons. */
  openOnClick?: boolean;
  onClick?: () => void;
  clickHint?: string;
  side?: EnhancedTooltipSide;
  maxWidthClass?: string;
  className?: string;
  appearance?: "default" | "alpha";
}

export interface TooltipPosition {
  top: number;
  left: number;
}

const DEFAULT_MAX_WIDTH = "max-w-xs";

/**
 * Keeps a fixed-positioned tooltip inside the viewport. Exported so other
 * portal-rendered tooltips (RestrictedPill) clamp identically instead of
 * reimplementing the arithmetic.
 */
export function clampTooltipPosition(
  triggerRect: DOMRect,
  tooltipRect: DOMRect,
  side: EnhancedTooltipSide
): TooltipPosition {
  const margin = 12;
  const gap = 8;
  let top = side === "bottom"
    ? triggerRect.bottom + gap
    : triggerRect.top - tooltipRect.height - gap;
  let left = triggerRect.left;

  if (left + tooltipRect.width > window.innerWidth - margin) {
    left = window.innerWidth - tooltipRect.width - margin;
  }
  if (left < margin) left = margin;

  if (top + tooltipRect.height > window.innerHeight - margin) {
    top = triggerRect.top - tooltipRect.height - gap;
  }
  if (top < margin) {
    top = triggerRect.bottom + gap;
  }

  return { top, left };
}

const EnhancedTooltip: React.FC<EnhancedTooltipProps> = ({
  content,
  title,
  triggerLabel,
  triggerVariant = "icon",
  triggerIcon,
  triggerClassName,
  ariaDisabled,
  openOnClick = false,
  onClick,
  clickHint,
  side = "bottom",
  maxWidthClass = DEFAULT_MAX_WIDTH,
  className = "",
  appearance = "default",
}) => {
  const isAlpha = appearance === "alpha";
  const tooltipId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [positioned, setPositioned] = useState(false);
  const [position, setPosition] = useState<TooltipPosition>({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) return;

    setPosition(clampTooltipPosition(trigger.getBoundingClientRect(), tooltip.getBoundingClientRect(), side));
  }, [side]);

  const show = useCallback(() => {
    setOpen(true);
  }, []);

  const hide = useCallback(() => {
    setOpen(false);
    setPositioned(false);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    setPositioned(true);
  }, [open, content, title, updatePosition]);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open || !openOnClick) return;
    const dismissOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !triggerRef.current?.contains(event.target)
        && !tooltipRef.current?.contains(event.target)) hide();
    };
    document.addEventListener("pointerdown", dismissOutside);
    return () => document.removeEventListener("pointerdown", dismissOutside);
  }, [open, openOnClick, hide]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (openOnClick) show();
    onClick?.();
  };

  const triggerClasses = isAlpha
    ? triggerVariant === "callout"
      ? "inline-flex items-center gap-1 rounded-v7-control border border-v7-ink/20 bg-v7-white px-1.5 py-0.5 text-[10px] font-semibold text-v7-ink transition-colors hover:border-v7-ink/40 focus:outline-none focus:ring-1 focus:ring-v7-bronze-dark"
      : "rounded-v7-control p-1 text-v7-ink/60 transition-colors hover:bg-v7-bronze/10 hover:text-v7-bronze-dark focus:outline-none focus:ring-2 focus:ring-v7-bronze/40"
    : triggerVariant === "callout"
    ? "inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-black transition-colors hover:border-gray-300 focus:outline-none focus:ring-1 focus:ring-rp-amber dark:border-gray-200 dark:bg-white dark:text-black dark:hover:border-gray-300"
    : "rounded-md p-1 text-gray-500 transition-colors hover:bg-rp-amber/10 hover:text-rp-amber focus:outline-none focus:ring-2 focus:ring-rp-amber/40 dark:text-gray-400 dark:hover:text-rp-amber";

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`${triggerClassName ?? triggerClasses} ${className}`.trim()}
        aria-label={triggerLabel}
        aria-disabled={ariaDisabled}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={onClick || openOnClick ? handleClick : undefined}
        onKeyDown={openOnClick ? event => {
          if (event.key === "Escape" && open) { event.stopPropagation(); hide(); }
        } : undefined}
      >
        {triggerVariant === "callout" ? (
          <>
            <CircleHelp className={`h-2.5 w-2.5 shrink-0 ${isAlpha ? "text-v7-ink" : "text-black"}`} />
            <span>{triggerLabel}</span>
          </>
        ) : (
          triggerIcon ?? <Info className="h-4 w-4 shrink-0" />
        )}
      </button>

      {open && createPortal(
        <div
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          style={{ position: "fixed", top: position.top, left: position.left }}
          data-appearance={appearance}
          className={`pointer-events-none z-[70] ${maxWidthClass} border px-4 py-3 shadow-xl transition-opacity duration-150 ${
            isAlpha
              ? "rounded-v7-popover border-v7-ink/15 bg-v7-white font-v7-sans"
              : "rounded-lg border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface-raised"
          }${positioned ? " opacity-100" : " opacity-0"}`}
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          {title && (
            <p className={`text-sm font-bold leading-tight ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>
              {title}
            </p>
          )}
          <div className={`text-sm leading-relaxed ${isAlpha ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-300"}${title ? " mt-1.5" : ""}`}>
            {content}
          </div>
          {onClick && (
            <p className={`mt-2 text-[10px] font-semibold uppercase tracking-wider ${isAlpha ? "text-v7-bronze-dark" : "text-rp-amber"}`}>
              {clickHint ?? "Click to learn more"}
            </p>
          )}
        </div>,
        document.body,
      )}
    </>
  );
};

export default EnhancedTooltip;
